/**
 * A person's own notification settings in this workspace (design 3.9, step N3), from the account
 * menu: how email arrives (immediate, a daily summary, or none), and what they follow or have muted.
 */
import { useState, type FormEvent } from "react";
import { useParams } from "react-router-dom";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { z } from "zod";
import { NotificationSettings, Subscription, type NotificationMode } from "@workcore/schemas";
import { checkedWrite, get } from "../api";
import { wsKey } from "../directory";
import { EmptyState, ErrorBox, Field, PageHeader, Panel, When, toast } from "../ui";

const ZONES: string[] = (() => {
  try {
    return (Intl as unknown as { supportedValuesOf(k: string): string[] }).supportedValuesOf("timeZone");
  } catch {
    return ["Europe/London", "UTC"];
  }
})();
const hour = (h: number) => `${String(h).padStart(2, "0")}:00`;

export function NotificationsPage() {
  const { slug = "" } = useParams();
  return (
    <>
      <PageHeader title="Notifications" description="How workcore emails you in this workspace, and what you follow. Mentions and assignments always reach you unless email is off." />
      <Settings slug={slug} />
      <Following slug={slug} />
    </>
  );
}

function Settings({ slug }: { slug: string }) {
  const q = useQueryClient();
  const current = useQuery({ queryKey: wsKey(slug, "notification-settings"), queryFn: async () => NotificationSettings.parse(await get(`/v1/ws/${slug}/me/notification-settings`)) });
  const [form, setForm] = useState<NotificationSettings | null>(null);
  const shown = form ?? current.data ?? null;
  const save = useMutation({
    mutationFn: (s: NotificationSettings) => checkedWrite("PUT", `/v1/ws/${slug}/me/notification-settings`, s),
    onSuccess: () => {
      setForm(null);
      toast("Saved");
      void q.invalidateQueries({ queryKey: wsKey(slug, "notification-settings") });
    },
  });
  if (!shown) return <ErrorBox error={current.error} />;
  const set = (p: Partial<NotificationSettings>) => setForm({ ...shown, ...p });
  const submit = (e: FormEvent) => {
    e.preventDefault();
    save.mutate(shown);
  };
  const modes: [NotificationMode, string, string][] = [
    ["immediate", "As it happens", "One email per item, a few minutes after the first change, so a burst of changes is one message."],
    ["digest", "Once a day", "A single summary of everything, at the hour you choose."],
    ["off", "No email", "Notifications are still listed in workcore."],
  ];
  return (
    <Panel title="Email">
      <form autoComplete="off" onSubmit={submit} className="stack">
        <fieldset className="choices" aria-label="How to email you">
          {modes.map(([key, label, help]) => (
            <label className="choice" key={key}>
              <input type="radio" name="mode" value={key} checked={shown.mode === key} onChange={() => set({ mode: key })} />
              <span><strong>{label}</strong><span className="muted small">{help}</span></span>
            </label>
          ))}
        </fieldset>
        {shown.mode === "digest" && (
          <div className="toolbar">
            <Field label="Send at">
              <select aria-label="Digest hour" value={shown.digestHour} onChange={(e) => set({ digestHour: Number(e.target.value) })}>
                {Array.from({ length: 24 }, (_, h) => <option key={h} value={h}>{hour(h)}</option>)}
              </select>
            </Field>
            <Field label="Time zone">
              <select aria-label="Time zone" value={shown.timezone} onChange={(e) => set({ timezone: e.target.value })}>
                {(ZONES.includes(shown.timezone) ? ZONES : [shown.timezone, ...ZONES]).map((z) => <option key={z} value={z}>{z.replace(/_/g, " ")}</option>)}
              </select>
            </Field>
          </div>
        )}
        <div className="actions">
          <button type="submit" disabled={!form || save.isPending}>Save</button>
          {form && <button type="button" className="secondary" onClick={() => setForm(null)}>Cancel</button>}
        </div>
        <ErrorBox error={save.error} />
      </form>
    </Panel>
  );
}

function Following({ slug }: { slug: string }) {
  const q = useQueryClient();
  const list = useQuery({ queryKey: wsKey(slug, "subscriptions"), queryFn: async () => z.array(Subscription).parse(await get(`/v1/ws/${slug}/me/subscriptions`)) });
  const clear = useMutation({
    mutationFn: (s: Subscription) => checkedWrite("DELETE", `/v1/ws/${slug}/objects/${s.objectId}/subscription`),
    onSuccess: () => void q.invalidateQueries({ queryKey: wsKey(slug, "subscriptions") }),
  });
  const rows = list.data ?? [];
  return (
    <Panel title="Following and muted">
      <p className="muted small">You follow what you create, own or are assigned to without doing anything. These are the items you chose to follow or mute yourself.</p>
      <ErrorBox error={list.error ?? clear.error} />
      {list.data && rows.length === 0 && <EmptyState title="Nothing chosen yet" body={<>Follow or mute an item from the work app, the CLI (<code>notify watch</code>, <code>notify mute</code>) or an email's unsubscribe link.</>} />}
      {rows.length > 0 && (
        <table className="grid">
          <thead><tr><th>Item</th><th>Setting</th><th>Since</th><th /></tr></thead>
          <tbody>
            {rows.map((s) => (
              <tr key={s.objectId}>
                <td>#{s.objectId} {s.objectName}</td>
                <td>{s.muted ? "Muted" : s.scope === "subtree" ? "Following, with everything under it" : "Following"}{s.muted && s.scope === "subtree" ? ", with everything under it" : ""}</td>
                <td><When at={s.createdAt} /></td>
                <td className="actions"><button className="link" onClick={() => clear.mutate(s)} aria-label={`Remove setting on #${s.objectId}`}>Remove</button></td>
              </tr>
            ))}
          </tbody>
        </table>
      )}
    </Panel>
  );
}
